import i18n from '@/i18n';

function normalizeTemuShopTypeLocal(shopType) {
  const raw = String(shopType || '').trim();
  if (!raw) return '';
  if (raw === '半托管' || raw === '全托管' || raw === '本土') return raw;
  if (raw === '本本' || raw === '本本店') return '本土';
  const upper = raw.toUpperCase();
  if (upper === 'BEN_BEN' || upper === 'LOCAL' || upper === 'L2L' || upper === 'LOCAL_TO_LOCAL' || upper === 'BEN_TU') {
    return '本土';
  }
  if (raw.includes('本本') || raw.includes('本土')) return '本土';
  if (raw.includes('半托')) return '半托管';
  if (raw.includes('全托')) return '全托管';
  return raw;
}

/**
 * 授权入口分两套卖家中心：跨境（Crossborder）走 agentseller-*，本土（Local）走 seller.temu.com。
 * <p>官方 Seller Authorization Guide 的卖家类型表：跨境只提供手动授权（Mannual）；
 * Callback / 应用内授权只对本土（Seller Central）开放，且路径是
 * {@code /open-platform/client-manage/authorization}（注意是 client-manage，
 * 不是跨境的 system-manage/client-manage）。
 */
const CROSSBORDER_AUTH_URL = {
  global: 'https://agentseller.temu.com/open-platform/system-manage/client-manage',
  eu: 'https://agentseller-eu.temu.com/open-platform/system-manage/client-manage',
  us: 'https://agentseller-us.temu.com/open-platform/system-manage/client-manage',
  product: 'https://agentseller.temu.com/open/system-manage/client-manage',
};

/** 本土 Seller Central 授权管理页（页面直接展示 access_token 供复制）。 */
const LOCAL_AUTH_URL = {
  global: 'https://seller.temu.com/open-platform/client-manage',
  eu: 'https://seller-eu.temu.com/open-platform/client-manage',
  us: 'https://seller.temu.com/open-platform/client-manage',
};

function authUrlMapForShopType(shopType) {
  return normalizeTemuShopTypeLocal(shopType) === '本土' ? LOCAL_AUTH_URL : CROSSBORDER_AUTH_URL;
}

/** 半托管：四槽 */
const SEMI_SLOTS = [
  { key: 'global', label: '全球Token' },
  { key: 'eu', label: '欧洲Token' },
  { key: 'us', label: '美区Token' },
  { key: 'product', label: '授权管理(商品)' },
];

/** 全托管：全球 + 授权管理(商品) */
const FULL_SLOTS = [
  { key: 'global', label: '全球Token' },
  { key: 'product', label: '授权管理(发品履约)' },
];

/** 本本：全球 + 美区/欧区发品履约（类型名已规范为「本土」） */
const LOCAL_SLOTS = [
  { key: 'global', label: '全球Token' },
  { key: 'us', label: '美区管理(发品履约)' },
  { key: 'eu', label: '欧区管理(发品履约)' },
];

/** @deprecated 请用 temuTokenSlotsForShopType；保留为半托管默认 */
export const TEMU_TOKEN_SLOTS = SEMI_SLOTS;

export function temuTokenSlotsForShopType(shopType) {
  const t = normalizeTemuShopTypeLocal(shopType);
  if (t === '全托管') return FULL_SLOTS;
  if (t === '本土') return LOCAL_SLOTS;
  return SEMI_SLOTS;
}

export function emptyTemuTokens() {
  return { global: '', eu: '', us: '', product: '' };
}

export function temuTokenSlotLabel(key, shopType) {
  const i18nKey = temuSlotI18nKey(key, shopType);
  if (i18nKey) return i18n.t(i18nKey, { ns: 'workbench' });
  const slots = temuTokenSlotsForShopType(shopType);
  const found = slots.find((s) => s.key === key);
  if (found) return found.label;
  const fallback = SEMI_SLOTS.find((s) => s.key === key);
  return fallback?.label || key || '-';
}

function temuSlotI18nKey(key, shopType) {
  const type = normalizeTemuShopTypeLocal(shopType);
  if (key === 'product') {
    return type === '全托管' ? 'shop.temuSlot.productFulfill' : 'shop.temuSlot.product';
  }
  if (key === 'us' && type === '本土') return 'shop.temuSlot.usFulfill';
  if (key === 'eu' && type === '本土') return 'shop.temuSlot.euFulfill';
  if (key === 'global') return 'shop.temuSlot.global';
  if (key === 'eu') return 'shop.temuSlot.eu';
  if (key === 'us') return 'shop.temuSlot.us';
  return '';
}

export function temuTokenSlotAuthUrl(key, shopType) {
  return authUrlMapForShopType(shopType)[key] || '';
}

/**
 * 授权（Token 获取）页需要勾选的 API 提示。
 * <p>注意：店铺数字 ID（mallId / supplierId）**不是**来自「按 token 查询授权信息」——
 * 该接口（CN {@code bg.open.accesstoken.info.get}）实测已下线（打 CN/PA 均 7000016），
 * PA 新 type {@code bg.open.accesstoken.info.get.global} 只回 {@code mallName} + 加密 {@code uniqueId}。
 * 店铺数字 ID 实际取自尺码表响应的 {@code result.sizeSpecDataList[].supplierId}
 * （PA {@code bg.glo.goods.sizecharts.get} / CN {@code bg.goods.sizecharts.get}）。
 * 打错 type/区会报 7000016；未勾选权限是 7000019。
 */
export const TEMU_AUTH_REQUIRED_API_HINT = [
  '基础API组：按 token 查询授权信息（仅 PA: bg.open.accesstoken.info.get.global；CN 旧 type 已废弃）',
  '基础API组：查询当前 token 对应店铺类型（bg.mall.info.get）',
  '货品API组、类目属性API组、图片视频API组（发品与同步商品必需）',
].join('；');

function authStatusText(status) {
  if (status === 'connected') return i18n.t('shop.authConnected', { ns: 'workbench' });
  if (status === 'expired') return i18n.t('shop.authExpired', { ns: 'workbench' });
  return i18n.t('shop.authDisconnected', { ns: 'workbench' });
}

function shortLabel(slot) {
  const label = String(slot?.label || '');
  if (slot.key === 'product') return '商品';
  if (label.includes('美区')) return '美区';
  if (label.includes('欧区') || label.includes('欧洲')) return '欧洲';
  if (label.includes('全球')) return '全球';
  return label.replace('Token', '').trim() || slot.key;
}

/** 列表展示：按店铺类型显示已填槽位 */
export function temuTokenFilledSummary(shop) {
  const filled = shop?.temuTokenFilled || {};
  const slots = temuTokenSlotsForShopType(shop?.shopType);
  const labels = slots.filter((s) => filled[s.key]).map(shortLabel);
  if (labels.length === 0) return '未授权 Token';
  return labels.join(' / ');
}

/**
 * 授权状态列：按店铺类型列出各 Token 槽状态（已授权 / 未授权 / 已过期）
 * @returns {{ key: string, label: string, status: 'connected'|'disconnected'|'expired', statusLabel: string }[]}
 */
export function temuTokenSlotAuthStates(shop) {
  const slots = temuTokenSlotsForShopType(shop?.shopType);
  const filled = shop?.temuTokenFilled || {};
  const shopExpired = String(shop?.authStatus || '') === 'expired'
    || isTokenExpiresAtPast(shop?.tokenExpiresAt);
  return slots.map((slot) => {
    const hasToken = Boolean(filled[slot.key]);
    let status = 'disconnected';
    if (hasToken && shopExpired) status = 'expired';
    else if (hasToken) status = 'connected';
    const statusLabel = authStatusText(status);
    return {
      key: slot.key,
      label: temuTokenSlotLabel(slot.key, shop?.shopType),
      status,
      statusLabel,
    };
  });
}

/** 列表授权列：优先展示已授权槽；若无则展示已过期槽；都没有再回退总状态文案。 */
export function temuAuthStatusColumnModel(shop) {
  const all = temuTokenSlotAuthStates(shop);
  const connected = all.filter((slot) => slot.status === 'connected');
  if (connected.length > 0) {
    return { kind: 'slots', slots: connected };
  }
  const expired = all.filter((slot) => slot.status === 'expired');
  if (expired.length > 0) {
    return { kind: 'slots', slots: expired };
  }
  const overall = String(shop?.authStatus || '');
  if (overall === 'connected' && shop?.realAuthBound !== false) {
    return { kind: 'badge', status: 'connected', label: authStatusText('connected') };
  }
  if (overall === 'expired') {
    return { kind: 'badge', status: 'expired', label: authStatusText('expired') };
  }
  return { kind: 'empty' };
}

function isTokenExpiresAtPast(raw) {
  if (raw == null || raw === '') return false;
  const t = Date.parse(String(raw));
  if (!Number.isFinite(t)) return false;
  return t <= Date.now();
}
